import { useRouter } from 'next/router'
import React, { ChangeEvent, useEffect, useState } from 'react'

import clsx from 'clsx'
import { UseFormReturn } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import { DEFAULT_CURRENCY } from '@/lib/constants'
import { DeviceOrderData } from '@/lib/schemes'
import { Device, OrderEstimate } from '@/lib/types'
import { formatCurrency } from '@/lib/utils'

import CountInput from '@/components/CountInput'
import Label from '@/components/Label'
import FormField from '@/components/form/FormField'
import RowSkeleton from '@/components/skeletons/RowSkeleton'

import ShopDiscountTag from './ShopDiscountTag'

const COUNT_STEP = 1

export const OrderAmountBlock = ({
  form,
  device,
  estimate,
  isLoading
}: {
  form: UseFormReturn<DeviceOrderData>
  device: Device
  estimate?: OrderEstimate
  isLoading: boolean
}) => {
  const { locale } = useRouter()
  const { formatMessage } = useIntl()

  const [prevMaxCount, setPrevMaxCount] = useState<number>()

  const { currency: deviceCurrency } = device
  const {
    discount,
    discountPercentage,
    hardwarePrice,
    maxHashrate,
    maxCount,
    nextDiscount
  } = estimate || {}

  const {
    watch,
    setValue,
    trigger,
    formState: { errors }
  } = form

  const isFullMiner = device.type === 'full'

  useEffect(() => {
    if (!!maxHashrate) setPrevMaxCount(maxHashrate)
    if (!!maxCount) setPrevMaxCount(maxCount)
  }, [maxHashrate, maxCount])

  const currency = deviceCurrency || DEFAULT_CURRENCY

  const priceWithDiscount =
    discount && hardwarePrice && hardwarePrice - discount

  return (
    <div className="flex select-none flex-col">
      <div className="flex flex-col gap-4 tablet:flex-row">
        <FormField
          className="w-full gap-0 self-end tablet:max-w-[263px]"
          error={errors.orderAmount}
          values={{ hardwareCount: prevMaxCount || '' }}
        >
          <Label>
            {isFullMiner ? (
              <FormattedMessage
                {...{
                  id: 'dialog.deviceSell.devices',
                  defaultMessage: 'Количество устройств'
                }}
              />
            ) : (
              <FormattedMessage
                {...{
                  id: 'dialog.deviceSell.countTH',
                  defaultMessage: 'Количество TH'
                }}
              />
            )}
          </Label>
          <CountInput
            max={prevMaxCount}
            min={1}
            step={COUNT_STEP}
            value={watch('orderAmount')}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setValue('orderAmount', Number(event.target?.value))
              trigger('orderAmount')
            }}
            suffix={
              isFullMiner
                ? formatMessage({
                    id: 'pieces',
                    defaultMessage: 'шт'
                  })
                : 'TH'
            }
          />
        </FormField>
        <div
          className={clsx(
            'flex items-start justify-between tablet:justify-start',
            'tablet:ml-auto tablet:flex-col tablet:items-end tablet:text-right'
          )}
        >
          <p className="text-x-base font-medium leading-x-sm">
            {isFullMiner ? (
              <FormattedMessage
                {...{
                  id: 'dialog.order.cost.device',
                  defaultMessage: 'Стоимость устройств'
                }}
              />
            ) : (
              <FormattedMessage
                {...{
                  id: 'dialog.order.cost.hashrate',
                  defaultMessage: 'Стоимость TH'
                }}
              />
            )}
          </p>
          <div className="flex flex-col items-end">
            <div className="flex items-center gap-2">
              {!isLoading && !!discount && (
                <span className="text-x-sm leading-x-sm text-secondary-500 line-through">
                  {formatCurrency(hardwarePrice || 0, currency, locale)}
                </span>
              )}
              <h3 className="tablet:leading-2xl text-xl font-medium leading-xl tablet:text-2xl">
                {isLoading ? (
                  <RowSkeleton className="h-8 w-32" />
                ) : (
                  !!(hardwarePrice || priceWithDiscount) &&
                  formatCurrency(
                    priceWithDiscount || hardwarePrice,
                    currency,
                    locale
                  )
                )}
              </h3>
            </div>
            {isLoading ? (
              <span className="mt-1 h-4 text-x-sm leading-x-sm text-secondary-500">
                <RowSkeleton className="h-[18px] w-24" />
              </span>
            ) : (
              !!(discountPercentage || nextDiscount?.count) && (
                <div className="flex flex-col items-end gap-1 phone:flex-row">
                  {!!nextDiscount?.count && (
                    <ShopDiscountTag>
                      <NextDiscount
                        {...{
                          nextDiscount,
                          currentCount: watch('orderAmount')
                        }}
                      />
                    </ShopDiscountTag>
                  )}
                  {!!discountPercentage && (
                    <ShopDiscountTag>
                      <Discount {...{ discountPercentage }} />
                    </ShopDiscountTag>
                  )}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export const Discount = ({
  discountPercentage
}: {
  discountPercentage?: number
}) => (
  <FormattedMessage
    {...{
      id: 'dialog.order.discount',
      defaultMessage: 'Скидка {discountPercentage}%',
      values: { discountPercentage }
    }}
  />
)

const NextDiscount = ({
  nextDiscount,
  currentCount
}: {
  nextDiscount: {
    count: number
    percentage: number
  }
  currentCount: number
}) => (
  <FormattedMessage
    {...{
      id: 'dialog.order.nextDiscount',
      defaultMessage:
        'Купите еще {count} и сэкономьте {nextDiscount?.percentage}%',
      values: {
        count: nextDiscount?.count - currentCount,
        percentage: nextDiscount?.percentage
      }
    }}
  />
)
